import { FormState, FieldState } from 'formstate';
import { FormValidators } from '@servicetitan/form';
import * as ApplicationValidators from './application-information-forms-validators';

export type LoanForm = FormState<{
    SalesAmount: FieldState<string>;
    DownPayment: FieldState<string>;
    Product: FieldState<string>;
}>;

export type DocumentForm = FormState<{
    Type: FieldState<string>;
    Number: FieldState<string>;
    State: FieldState<string>;
    IssueDate: FieldState<string>;
    ExpirationDate: FieldState<string>;
}>;

export type AddressForm = FormState<{
    Street: FieldState<string>;
    Unit: FieldState<string>;
    City: FieldState<string>;
    State: FieldState<string>;
    Zip: FieldState<string>;
}>;

export type MarriageForm = FormState<{
    isMarried: FieldState<boolean>;
    ContactInfo: FieldState<string>;
}>;

export type ResidenceForm = FormState<{
    HousingType: FieldState<string>;
    MonthlyPayment: FieldState<string>;
    PropertyType: FieldState<string>;
}>;

export type PersonalInfoForm = FormState<{
    FirstName: FieldState<string>;
    MiddleName: FieldState<string>;
    LastName: FieldState<string>;
    DateOfBirth: FieldState<string>;
    Ssn: FieldState<string>;
    DisplaySsn: FieldState<string>;
    Email: FieldState<string>;
    PhoneNumber: FieldState<string>;
    EmployerName: FieldState<string>;
    GrossMonthlyIncome: FieldState<string>;
    IsUSCitizen: FieldState<boolean>;
}>;

export type ApplicantForm = FormState<{
    PersonalInfo: PersonalInfoForm;
    HomeAddress: AddressForm;
    Document: DocumentForm;
}>;

export type CoApplicantForm = FormState<{
    PersonalInfo: PersonalInfoForm;
    HomeAddress: AddressForm;
    Document: DocumentForm;
    SameAsPrimaryAddress: FieldState<boolean>;
}>;

export type ApplicationForm = FormState<{
    Loan: LoanForm;
    PrimaryApplicant: ApplicantForm;
    WorkingAddressSameAsPrimaryAddress: FieldState<boolean>;
    WorkingAddress: AddressForm;
    Marriage: MarriageForm;
    Residence: ResidenceForm;
    hasCoApplicant: FieldState<boolean>;
    CoApplicant: CoApplicantForm;
}>;

const createEmptyLoanForm = (): LoanForm =>
    new FormState({
        SalesAmount: new FieldState<string>('')
            .disableAutoValidation()
            .validators(ApplicationValidators.minimumAmountValidator),
        DownPayment: new FieldState<string>('').disableAutoValidation(),
        Product: new FieldState<string>('')
            .disableAutoValidation()
            .validators(FormValidators.required),
    });

const createEmptyDocumentForm = (): DocumentForm =>
    new FormState({
        Type: new FieldState<string>('')
            .disableAutoValidation()
            .validators(FormValidators.required),
        Number: new FieldState<string>('')
            .disableAutoValidation()
            .validators(FormValidators.required),
        State: new FieldState<string>('').disableAutoValidation(),
        IssueDate: new FieldState<string>('')
            .disableAutoValidation()
            .validators(ApplicationValidators.issueDateValidator),
        ExpirationDate: new FieldState<string>('')
            .disableAutoValidation()
            .validators(ApplicationValidators.expDateValidator),
    });

const createEmptyAddressForm = (): AddressForm =>
    new FormState({
        Street: new FieldState<string>('')
            .disableAutoValidation()
            .validators(FormValidators.required),
        Unit: new FieldState<string>(''),
        City: new FieldState<string>('')
            .disableAutoValidation()
            .validators(FormValidators.required),
        State: new FieldState<string>('')
            .disableAutoValidation()
            .validators(FormValidators.required),
        Zip: new FieldState<string>('').disableAutoValidation().validators(FormValidators.required),
    });

const createEmptyMarriageForm = (): MarriageForm =>
    new FormState({
        isMarried: new FieldState<boolean>(false),
        ContactInfo: new FieldState<string>('')
            .disableAutoValidation()
            .validators(ApplicationValidators.spouseContactValidator),
    });

const createEmptyResidenceForm = (): ResidenceForm =>
    new FormState({
        HousingType: new FieldState<string>('Own')
            .disableAutoValidation()
            .validators(FormValidators.required),
        MonthlyPayment: new FieldState<string>('')
            .disableAutoValidation()
            .validators(ApplicationValidators.housingPaymentValidator),
        PropertyType: new FieldState<string>('Single Family')
            .disableAutoValidation()
            .validators(FormValidators.required),
    });

const createEmptyPersonalInfoForm = (): PersonalInfoForm =>
    new FormState({
        FirstName: new FieldState<string>('')
            .disableAutoValidation()
            .validators(FormValidators.required),
        MiddleName: new FieldState<string>(''),
        LastName: new FieldState<string>('')
            .disableAutoValidation()
            .validators(FormValidators.required),
        DateOfBirth: new FieldState<string>('')
            .disableAutoValidation()
            .validators(ApplicationValidators.ageValidator),
        Ssn: new FieldState<string>('')
            .disableAutoValidation()
            .validators(ApplicationValidators.ssnValidator),
        DisplaySsn: new FieldState<string>('')
            .disableAutoValidation()
            .validators(ApplicationValidators.ssnValidator),
        Email: new FieldState<string>('')
            .disableAutoValidation()
            .validators(ApplicationValidators.emailValidator),
        PhoneNumber: new FieldState<string>('')
            .disableAutoValidation()
            .validators(ApplicationValidators.phoneNumberValidator),
        EmployerName: new FieldState<string>('')
            .disableAutoValidation()
            .validators(FormValidators.required),
        GrossMonthlyIncome: new FieldState<string>('')
            .disableAutoValidation()
            .validators(ApplicationValidators.minimumAmountValidator),
        IsUSCitizen: new FieldState<boolean>(true).disableAutoValidation(),
    });

const createEmptyApplicantForm = (): ApplicantForm =>
    new FormState({
        PersonalInfo: createEmptyPersonalInfoForm(),
        HomeAddress: createEmptyAddressForm(),
        Document: createEmptyDocumentForm(),
    });

const createEmptyCoApplicantForm = (): CoApplicantForm =>
    new FormState({
        PersonalInfo: createEmptyPersonalInfoForm(),
        HomeAddress: createEmptyAddressForm(),
        Document: createEmptyDocumentForm(),
        SameAsPrimaryAddress: new FieldState<boolean>(false),
    });

export const createEmptyApplicationForm = (): ApplicationForm =>
    new FormState({
        Loan: createEmptyLoanForm(),
        PrimaryApplicant: createEmptyApplicantForm(),
        WorkingAddressSameAsPrimaryAddress: new FieldState<boolean>(true),
        WorkingAddress: createEmptyAddressForm(),
        Marriage: createEmptyMarriageForm(),
        Residence: createEmptyResidenceForm(),
        hasCoApplicant: new FieldState<boolean>(false),
        CoApplicant: createEmptyCoApplicantForm(),
    });
